import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

const createLead = async (values: any) => {
    const leadData = {
        params: {
            name: values.fullName,
            phone: values.phone,
            opportunity: "Real Estate Campaign - Interactive Funnel",
            salesperson_id: 17,
            company_id: 2,
            email_from: values.email || "",
            contact_name: values.fullName,
            city: values.city || "",
            description: `
Owner: ${values.isOwner || "N/A"}
Project Type: ${values.projectType || "N/A"}
Project Status: ${values.projectStatus || "N/A"}
Running Digital Ads: ${values.digitalAds || "N/A"}
Biggest Challenge: ${values.biggestChallenge || "N/A"}
Marketing Budget: ${values.investBudget || "N/A"}
Start Working: ${values.startWorking || "N/A"}
Website: ${values.websiteLink || "N/A"}
Preferred Connection Time: ${values.connectionTime || "N/A"}
Submission Time: ${new Date().toLocaleString()}
      `.trim(),
        },
    };

    const response = await axios.post(
        "https://mysamplewebsite.in/api/crm_leads/create",
        leadData,
        {
            headers: {
                "Content-Type": "application/json",
            },
        }
    );

    if (response.data.status !== "success") {
        throw new Error(response.data.message || "Failed to create lead");
    }

    return response.data;
};

const questions = [
    { id: "intro", type: "intro" },
    {
        id: "isOwner",
        title: "Are you the owner? ",
        type: "radio",
        options: ["Yes", "No"],
    },
    {
        id: "projectType",
        title: "What is your project type? ",
        type: "radio",
        options: ["Commercial", "Residential", "Mixed Use"],
    },
    {
        id: "projectStatus",
        title: "What is the current status of project? ",
        type: "radio",
        options: [
            "Planning Phase",
            "Pre Launch Phase",
            "Launch Phase",
            "Post-Launch / Active Sales Phase",
            "Mid-Sales Phase",
            "Possession Phase",
        ],
    },
    {
        id: "digitalAds",
        title: "Are you currently running digital ads or working with any marketing agency? ",
        type: "radio",
        options: [
            "Yes, currently active",
            "Worked before but paused",
            "No, never run ads before",
        ],
    },
    {
        id: "biggestChallenge",
        title: "What’s your biggest challenge right now? ",
        type: "radio",
        options: [
            "Getting enough leads",
            "Converting leads into visits",
            "Managing CRM & follow-ups",
            "Tracking team performance",
        ],
    },
    {
        id: "investBudget",
        title: "How much are you looking to invest in advertisements for acquiring new customers? ",
        type: "radio",
        options: [
            "₹0 - 50,000 (Hey you need to allocate at least  ₹80K - 1 Lac budget & we will show you our wonders, guaranteed)",
            "₹50K - 1 Lac",
            "₹1Lac - 1.5 Lacs",
            "₹1.5Lacs - 2 Lacs",
            "₹2 Lacs - 5 Lacs",
            "₹5 Lacs - 10 Lacs",
            "₹10 Lacs - 30 Lacs",
            "₹30 Lacs - 1 Cr",
            "₹1Cr Above",
        ],
    },
    {
        id: "startWorking",
        title: "How soon are you looking to start working with a new marketing partner? ",
        type: "radio",
        options: [
            "Immediately",
            "Within 15 days",
            "Within a month",
            "Just exploring options",
        ],
    },
    { id: "final", type: "final" },
];

const validationSchemas = [
    Yup.object({}), // Intro schema
    Yup.object({ isOwner: Yup.string().required("Please select an option") }),
    Yup.object({ projectType: Yup.string().required("Please select an option") }),
    Yup.object({ projectStatus: Yup.string().required("Please select an option") }),
    Yup.object({ digitalAds: Yup.string().required("Please select an option") }),
    Yup.object({ biggestChallenge: Yup.string().required("Please select an option") }),
    Yup.object({ investBudget: Yup.string().required("Please select an option") }),
    Yup.object({ startWorking: Yup.string().required("Please select an option") }),
    Yup.object({
        websiteLink: Yup.string(),
        connectionTime: Yup.string().required("Connection time is required"),
        fullName: Yup.string().required("Full name is required"),
        email: Yup.string().email("Invalid email").required("Email is required"),
        phone: Yup.string()
            .matches(/^[0-9]{10,}$/, "Phone number must contain at least 10 digits.")
            .required("Phone number is required"),
        city: Yup.string().required("City is required"),
    }),
];

const InteractiveRealEstate = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);

    useEffect(() => {
        if (window.fbq) {
            window.fbq('init', '908807021962260');
            window.fbq('track', 'PageView');
        }
    }, []);
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Enter") {
                e.preventDefault();
                formik.handleSubmit();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [step]);

    const currentSchema = validationSchemas[step];

    const formik = useFormik({
        initialValues: {
            isOwner: "",
            projectType: "",
            projectStatus: "",
            digitalAds: "",
            biggestChallenge: "",
            investBudget: "",
            startWorking: "",
            websiteLink: "",
            connectionTime: "",
            fullName: "",
            email: "",
            phone: "",
            city: "",
        },
        validationSchema: currentSchema,
        validateOnChange: false,
        onSubmit: async (values, actions) => {
            if (step === questions.length - 1) {
                toast.loading("Securing your details...", { id: "submit" });
                try {
                    await createLead(values);
                    toast.success("Details secured.", { id: "submit" });
                    setTimeout(() => {
                        navigate("/thankyou-realestate");
                    }, 1000);
                } catch (error: any) {
                    toast.error(
                        error.response?.data?.message ||
                        error.message ||
                        "An error occurred.",
                        { id: "submit" }
                    );
                } finally {
                    actions.setSubmitting(false);
                }
            } else {
                setIsAnimating(true);
                setTimeout(() => {
                    setStep((prev) => prev + 1);
                    setIsAnimating(false);
                }, 300);
                actions.setTouched({});
                actions.setSubmitting(false);
            }
        },
    });

    const nextStep = () => formik.handleSubmit();

    const prevStep = () => {
        if (step > 0) {
            setIsAnimating(true);
            setTimeout(() => {
                setStep((prev) => prev - 1);
                setIsAnimating(false);
            }, 300);
        }
    };

    const handleRadioSelect = (field: string, value: string) => {
        formik.setFieldValue(field, value);
        setTimeout(() => nextStep(), 400);
    };

    const renderIntro = () => {
        return (
            <div className={`flex flex-col transition-all duration-500 ${step === 0 ? "pt-10 items-center text-center m-auto max-w-4xl" : "pt-0 items-start"}`}>
                <span className={`text-[#3E88FF] uppercase tracking-widest font-bold text-xs border-b border-[#3E88FF] pb-1 transition-all ${step === 0 ? "mb-6" : "mb-4"}`}>Exclusive Partnership</span>
                <h1 className={`font-light text-slate-100 leading-tight font-serif tracking-wide transition-all duration-500 ${step === 0 ? "text-5xl md:text-6xl mb-6" : "text-2xl lg:text-3xl xl:text-4xl mb-4"}`}>
                    Meet India's Only <br /><strong className="font-bold text-white">Real Estate Site Visit <br />Acquisition</strong> Experts.
                </h1>
                <div className={`h-1 w-24 bg-[#3E88FF] transition-all ${step === 0 ? "mb-10" : "mb-6"}`}></div>
                <div className={`text-slate-300 transition-all duration-500 ${step === 0 ? "text-lg space-y-6 max-w-2xl flex flex-col items-start mx-auto w-fit text-left" : "w-full text-xs lg:text-sm xl:text-base space-y-3 opacity-90 max-w-xl text-left"}`}>
                    <div className="flex items-start text-left">
                        <div className="mt-2 h-2 w-2 bg-[#3E88FF] rounded-sm shrink-0 mr-4"></div>
                        <p>We provide comprehensive <strong>tele sales support</strong> and guarantee <strong>confirmed site visits</strong>.</p>
                    </div>
                    <div className="flex items-start text-left">
                        <div className="mt-2 h-2 w-2 bg-[#3E88FF] rounded-sm shrink-0 mr-4"></div>
                        <p>Our reporting is strictly focused on <strong>confirmed visits</strong> & <strong>client walk-ins</strong>.</p>
                    </div>
                    <div className="flex items-start text-left">
                        <div className="mt-2 h-2 w-2 bg-[#3E88FF] rounded-sm shrink-0 mr-4"></div>
                        <p>Discover our proven methodology in a <strong>20-minute discovery call</strong>.</p>
                    </div>
                </div>
                {step === 0 && (
                    <button
                        onClick={nextStep}
                        className="mt-16 bg-[#3E88FF] hover:bg-blue-600 text-white font-bold py-4 px-10 rounded-sm shadow-2xl transition-all duration-300 flex items-center justify-between min-w-[250px] uppercase tracking-widest text-sm group"
                    >
                        Start Assessment <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                    </button>
                )}
            </div>
        );
    };

    const renderRadioQuestion = (q: any) => {
        return (
            <div className="w-full max-w-2xl">
                <div className="text-slate-500 font-bold tracking-widest text-xs mb-4 uppercase flex items-center">
                    <span className="w-8 h-[1px] bg-slate-300 mr-3"></span> Phase {step} / {questions.length - 1}
                </div>
                <h2 className="text-3xl md:text-4xl font-light text-slate-800 mb-12 leading-snug">
                    {q.title}
                </h2>
                <div className="grid grid-cols-1 gap-4">
                    {q.options.map((option: string, index: number) => {
                        const isSelected = formik.values[q.id as keyof typeof formik.values] === option;
                        const letter = String.fromCharCode(65 + index);
                        return (
                            <div
                                key={option}
                                onClick={() => handleRadioSelect(q.id, option)}
                                className={`relative flex items-center px-6 py-5 cursor-pointer transition-all duration-300 border-l-4 group
                    ${isSelected ? "border-[#3E88FF] bg-[#3E88FF]/5" : "border-slate-200 bg-white hover:border-slate-400 hover:bg-slate-50"}`}
                            >
                                <div className={`text-xs font-bold mr-6 transition-colors ${isSelected ? "text-[#3E88FF]" : "text-slate-400"}`}>
                                    {letter}
                                </div>
                                <span className={`text-lg font-medium transition-colors ${isSelected ? "text-slate-900" : "text-slate-600 group-hover:text-slate-800"}`}>
                                    {option}
                                </span>
                            </div>
                        );
                    })}
                </div>
                {formik.errors[q.id as keyof typeof formik.errors] && (
                    <div className="mt-6 text-red-500 text-sm font-medium border-l-2 border-red-500 pl-3">
                        {formik.errors[q.id as keyof typeof formik.errors] as string}
                    </div>
                )}
            </div>
        );
    };

    const renderFinal = () => {
        return (
            <div className="w-full max-w-2xl">
                <div className="text-[#3E88FF] font-bold tracking-widest text-xs mb-4 uppercase flex items-center">
                    <span className="w-8 h-[1px] bg-[#3E88FF] mr-3"></span> Final Phase
                </div>
                <h2 className="text-3xl font-light text-slate-800 mb-10">Provide your contact details.</h2>

                <div className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="relative border-b-2 border-slate-200 focus-within:border-[#3E88FF] transition-colors">
                            <input
                                type="text"
                                name="fullName"
                                placeholder="Full Name *"
                                className="w-full bg-transparent px-0 py-3 text-lg focus:outline-none text-slate-800 font-medium placeholder-slate-400"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.fullName}
                            />
                            {formik.touched.fullName && formik.errors.fullName && <div className="text-red-500 text-xs mt-1 absolute -bottom-5 left-0">{formik.errors.fullName}</div>}
                        </div>
                        <div className="relative border-b-2 border-slate-200 focus-within:border-[#3E88FF] transition-colors">
                            <input
                                type="email"
                                name="email"
                                placeholder="Email Address *"
                                className="w-full bg-transparent px-0 py-3 text-lg focus:outline-none text-slate-800 font-medium placeholder-slate-400"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.email}
                            />
                            {formik.touched.email && formik.errors.email && <div className="text-red-500 text-xs mt-1 absolute -bottom-5 left-0">{formik.errors.email}</div>}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="relative border-b-2 border-slate-200 focus-within:border-[#3E88FF] transition-colors">
                            <input
                                type="tel"
                                name="phone"
                                placeholder="Phone Number *"
                                className="w-full bg-transparent px-0 py-3 text-lg focus:outline-none text-slate-800 font-medium placeholder-slate-400"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.phone}
                            />
                            {formik.touched.phone && formik.errors.phone && <div className="text-red-500 text-xs mt-1 absolute -bottom-5 left-0">{formik.errors.phone}</div>}
                        </div>
                        <div className="relative border-b-2 border-slate-200 focus-within:border-[#3E88FF] transition-colors">
                            <input
                                type="text"
                                name="city"
                                placeholder="City *"
                                className="w-full bg-transparent px-0 py-3 text-lg focus:outline-none text-slate-800 font-medium placeholder-slate-400"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.city}
                            />
                            {formik.touched.city && formik.errors.city && <div className="text-red-500 text-xs mt-1 absolute -bottom-5 left-0">{formik.errors.city}</div>}
                        </div>
                    </div>

                    <div className="relative border-b-2 border-slate-200 focus-within:border-[#3E88FF] transition-colors">
                        <input
                            type="text"
                            name="connectionTime"
                            placeholder="When would you like to connect? (Date & Time) *"
                            className="w-full bg-transparent px-0 py-3 text-lg focus:outline-none text-slate-800 font-medium placeholder-slate-400"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.connectionTime}
                        />
                        {formik.touched.connectionTime && formik.errors.connectionTime && <div className="text-red-500 text-xs mt-1 absolute -bottom-5 left-0">{formik.errors.connectionTime}</div>}
                    </div>

                    <div className="relative border-b-2 border-slate-200 focus-within:border-[#3E88FF] transition-colors">
                        <input
                            type="text"
                            name="websiteLink"
                            placeholder="Website Link"
                            className="w-full bg-transparent px-0 py-3 text-lg focus:outline-none text-slate-800 font-medium placeholder-slate-400"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.websiteLink}
                        />
                        {formik.touched.websiteLink && formik.errors.websiteLink && <div className="text-red-500 text-xs mt-1 absolute -bottom-5 left-0">{formik.errors.websiteLink}</div>}
                    </div>
                </div>

                <button
                    onClick={nextStep}
                    className="mt-14 bg-slate-900 hover:bg-[#3E88FF] text-white font-bold py-4 px-12 transition-all duration-300 flex items-center justify-between uppercase tracking-widest text-sm w-full sm:w-auto hover:px-14 disabled:opacity-50"
                >
                    Submit Request <ArrowRight className="w-5 h-5 ml-4" />
                </button>
            </div>
        );
    };

    const getProgress = () => ((step) / (questions.length - 1)) * 100;

    return (
        <div className="min-h-screen font-poppins flex flex-col md:flex-row bg-slate-50 selection:bg-[#3E88FF] selection:text-white">
            <Toaster position="top-center" />

            {/* Split Layout: Dark Left Pane (only visible completely on Intro, then shrinks to a sidebar) */}
            <div className={`transition-all duration-1000 ease-in-out bg-[#0b1324] relative overflow-hidden flex flex-col
         ${step === 0 ? "w-full p-10 md:p-20 justify-center min-h-[60vh] md:min-h-screen" : "w-full md:w-1/3 p-10 justify-between min-h-[30vh] md:min-h-screen hidden md:flex"}`}
            >
                {/* Subtle architectural lines pattern overlay */}
                <div className="absolute inset-0 opacity-[0.03] bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

                <div className={`relative z-10 w-full ${step === 0 ? "text-center flex flex-col items-center" : ""}`}>
                    <h1 className="text-white text-3xl font-anton tracking-widest cursor-pointer transition-all duration-500 flex items-baseline" onClick={() => navigate("/")}>
                        DUMOSH<span className="h-2 w-2 bg-[#FF3E3E] rounded-full ml-0.5"></span>
                    </h1>
                    {renderIntro()}
                </div>

                {/* Steps indicator for side bar when not on intro */}
                {step > 0 && (
                    <div className="relative z-10 w-full mt-auto mb-10">
                        <div className="text-slate-400 text-sm tracking-widest uppercase mb-4 font-bold border-b border-white/10 pb-4">Progress</div>
                        <div className="flex items-center">
                            <span className="text-white text-4xl font-light mr-4">{step}</span>
                            <span className="text-slate-500 text-xl font-light">/ {questions.length - 1}</span>
                        </div>
                        <div className="w-full h-[1px] bg-slate-800 mt-6 relative">
                            <div className="absolute top-0 left-0 h-[2px] bg-[#3E88FF] transition-all duration-500" style={{ width: `${getProgress()}%` }}></div>
                        </div>
                    </div>
                )}
            </div>

            {/* Right Pane (Form Area) */}
            {step > 0 && (
                <div className={`transition-all duration-1000 ease-in-out bg-white relative flex flex-col justify-center w-full md:w-2/3 p-8 md:p-16 lg:p-24 min-h-[70vh] md:min-h-screen`}
                >
                    <div className="w-full flex flex-col justify-center items-center">
                        {/* Mobile header / Back button */}
                        <div className="md:hidden flex flex-col items-center w-full mb-12 border-b border-gray-100 pb-6">
                            <h1 className="text-slate-900 text-3xl font-anton tracking-widest mb-4 flex items-baseline" onClick={() => navigate("/")}>
                                DUMOSH<span className="h-2 w-2 bg-[#FF3E3E] rounded-full ml-0.5"></span>
                            </h1>
                            <div className="flex items-center space-x-3">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">Assessment in Progress</span>
                                <span className="text-[10px] font-black text-[#3E88FF] bg-[#3E88FF]/10 px-2 py-0.5 rounded-full">{Math.round(getProgress())}%</span>
                            </div>
                        </div>

                        <div
                            className={`w-full max-w-3xl transition-opacity duration-500 ${isAnimating ? "opacity-0" : "opacity-100"}`}
                        >
                            {step < questions.length - 1 ? renderRadioQuestion(questions[step]) : renderFinal()}
                        </div>
                    </div>
                </div>
            )}

            {/* Floating Navigation Controls (Right Side) */}
            {step > 0 && (
                <div className="fixed bottom-6 md:bottom-12 right-6 md:right-12 z-50 flex gap-4">
                    <button
                        onClick={prevStep}
                        className="w-12 h-12 flex items-center justify-center bg-white border border-slate-200 text-slate-400 hover:text-slate-800 hover:border-slate-400 shadow-sm transition-all rounded-sm cursor-pointer"
                        title="Previous"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <button
                        onClick={nextStep}
                        className="w-12 h-12 flex items-center justify-center bg-slate-900 text-white hover:bg-[#3E88FF] shadow-lg transition-all rounded-sm cursor-pointer"
                        title="Next"
                    >
                        <ArrowRight className="w-5 h-5" />
                    </button>
                </div>
            )}
        </div>
    );
};

export default InteractiveRealEstate;
